import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import ErrorMessage from '@/models/Shared/ErrorMessage';
import NewWorkerViewModel from '@/models/Workers/NewWorkerViewModel';
import CreateWorkerRequest from '@/models/Workers/CreateWorkerRequest';
import WorkerDto from '@/models/Workers/WorkerDto';
import { CVParseResult } from '@/models/CVParser/CVParseResult';
import WorkType from '@/models/Shared/WorkType';
import CheckWorkerDuplicatesRequest from '@/models/Workers/CheckWorkerDuplicatesRequest';
import CheckWorkerDuplicatesResponse from '@/models/Workers/CheckWorkerDuplicatesResponse';
import DuplicateWorkerMatch from '@/models/Workers/DuplicateWorkerMatch';
import MergeWorkerRequest, { MergeWorkerFieldUpdates, UpdateWorkerPositionForMerge, MatchedAttributeForMerge } from '@/models/Workers/MergeWorkerRequest';
import { startCVParseWithFirestore, CV_PROGRESS_STEPS } from '@/lib/cvParserFirestore';
import { Unsubscribe } from 'firebase/firestore';

// CV streaming state for real-time progress
export interface CVStreamState {
  isStreaming: boolean;
  currentStep: number;
  totalSteps: number;
  stepMessage: string;
  thinkingContent: string;
  error: string | null;
}

export interface CVImportSummary {
  positionCount: number;
  skillCount: number;
  qualificationCount: number;
}

// Field choice for merge operations - 'new' means keep wizard value, 'existing' means keep existing worker value
export type FieldChoice = 'new' | 'existing';
export type KeepDeleteChoice = 'keep' | 'delete';

// For positions that exist in both, track field-level choices
export interface PositionFieldChoices {
  workTypes: FieldChoice;  // isTemp/isPerm - if 'new', use wizard values
  source: FieldChoice;     // if 'new', use wizard source/agentMatchReason/agentMatchConfidence/agenticTaskId
  payRate: FieldChoice;    // if 'new', use wizard payRate
  isVerified: FieldChoice; // if 'new', use wizard isVerified value
}

// Tracks user's selections for which values to keep during merge
export interface MergeSelections {
  fieldChoices: Record<string, FieldChoice>;
  // Position field-level choices for positions that exist in both
  positionFieldChoices: Record<string, PositionFieldChoices>;
  // Skill/Qualification isVerified choices for items that exist in both
  skillIsVerifiedChoices: Record<string, FieldChoice>;
  qualificationIsVerifiedChoices: Record<string, FieldChoice>;
  // Keep/Delete for existing-only attributes
  existingPositionChoices: Record<string, KeepDeleteChoice>;
  existingSkillChoices: Record<string, KeepDeleteChoice>;
  existingQualificationChoices: Record<string, KeepDeleteChoice>;
  // Keep/Delete for existing-only availability slots
  existingAvailabilityChoices: Record<string, KeepDeleteChoice>;
}

// Field comparison data for merge review UI
export interface FieldComparison {
  field: string;
  label: string;
  newValue: any;
  existingValue: any;
  newDisplayValue: string;
  existingDisplayValue: string;
  choice: FieldChoice;
  isDifferent: boolean;
}

// Position comparison for positions that exist in both wizard and existing worker
export interface PositionMergeComparison {
  attributeId: string;
  name: string;
  newPosition: any;
  existingPosition: any;
  // Field-level choices
  choices: PositionFieldChoices;
  // Display values for comparison
  newWorkTypes: { isTemp: boolean; isPerm: boolean };
  existingWorkTypes: { isTemp: boolean; isPerm: boolean };
  newPayRate: number;
  existingPayRate: number;
  newIsVerified: boolean;
  existingIsVerified: boolean;
  hasNewSource: boolean;  // Whether wizard has source info (agent match data)
}

// Skill comparison for skills that exist in both wizard and existing worker
export interface SkillMergeComparison {
  attributeId: string;
  name: string;
  newSkill: any;
  existingSkill: any;
  newIsVerified: boolean;
  existingIsVerified: boolean;
  isVerifiedChoice: FieldChoice;
}

// Qualification comparison for qualifications that exist in both wizard and existing worker
export interface QualificationMergeComparison {
  attributeId: string;
  name: string;
  newQualification: any;
  existingQualification: any;
  newIsVerified: boolean;
  existingIsVerified: boolean;
  isVerifiedChoice: FieldChoice;
}

// Smart default helper functions for merge field selection
function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (value === '') return true;
  if (typeof value === 'number' && value === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function isUnknownGender(value: any): boolean {
  // Gender enum: 0 = Unknown, 1 = Male, 2 = Female
  return value === 0 || value === 'Unknown';
}

function isUnknownMaritalStatus(value: any): boolean {
  // MaritalStatus enum: 0 = Unknown
  return value === 0 || value === 'Unknown';
}

function areValuesEqual(newValue: any, existingValue: any): boolean {
  // Handle null/undefined equality
  if (isEmpty(newValue) && isEmpty(existingValue)) return true;
  if (newValue === existingValue) return true;
  // Handle date comparison (strip time component)
  if (newValue instanceof Date && existingValue instanceof Date) {
    return newValue.toDateString() === existingValue.toDateString();
  }
  // Handle string date comparison
  if (typeof newValue === 'string' && typeof existingValue === 'string') {
    // Try to compare as dates if they look like dates
    if (newValue.includes('-') && existingValue.includes('-')) {
      return newValue.split('T')[0] === existingValue.split('T')[0];
    }
  }
  return JSON.stringify(newValue) === JSON.stringify(existingValue);
}

// Normalize name from CV parser - only convert to title case if entire name is ALL CAPS
// This preserves intentional mixed case like "McDonald" or "O'Brien"
function normalizeNameFromCV(name: string | undefined | null): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (!trimmed) return '';

  // Check if the entire name is uppercase (ignoring non-letter characters)
  const letters = trimmed.replace(/[^a-zA-Z]/g, '');
  if (letters.length === 0) return trimmed;

  const isAllCaps = letters === letters.toUpperCase();

  if (isAllCaps) {
    // Convert to title case, capitalizing after hyphens and apostrophes
    // e.g., O'BRIEN → O'Brien, JEAN-PIERRE → Jean-Pierre
    let result = '';
    let capitalizeNext = true;

    for (const char of trimmed) {
      if (char === '-' || char === "'") {
        result += char;
        capitalizeNext = true;
      } else if (capitalizeNext && /[a-zA-Z]/.test(char)) {
        result += char.toUpperCase();
        capitalizeNext = false;
      } else {
        result += char.toLowerCase();
      }
    }

    return result;
  }

  // Not all caps - preserve the original casing (e.g., "McDonald")
  return trimmed;
}

function getSmartDefault(field: string, newValue: any, existingValue: any): FieldChoice {
  // Rule 1: If values are identical, prefer existing (no change needed)
  if (areValuesEqual(newValue, existingValue)) return 'existing';

  // Rule 2: Special handling for gender - prefer the one that isn't Unknown
  if (field === 'gender') {
    if (isUnknownGender(newValue) && !isUnknownGender(existingValue)) return 'existing';
    if (!isUnknownGender(newValue) && isUnknownGender(existingValue)) return 'new';
  }

  // Rule 3: Special handling for marital status - prefer the one that isn't Unknown
  if (field === 'maritalStatus') {
    if (isUnknownMaritalStatus(newValue) && !isUnknownMaritalStatus(existingValue)) return 'existing';
    if (!isUnknownMaritalStatus(newValue) && isUnknownMaritalStatus(existingValue)) return 'new';
  }

  // Rule 4: Special handling for isExactGeo - prefer exact (true) over approximate (false)
  if (field === 'isExactGeo') {
    if (existingValue === true && newValue !== true) return 'existing';
    if (newValue === true && existingValue !== true) return 'new';
  }

  // Rule 5: If new value is empty but existing has value, prefer existing
  if (isEmpty(newValue) && !isEmpty(existingValue)) return 'existing';

  // Rule 6: If existing is empty but new has value, prefer new
  if (!isEmpty(newValue) && isEmpty(existingValue)) return 'new';

  // Rule 7: Both have different valid values - prefer new (user just entered it deliberately)
  return 'new';
}

// Field labels for display in merge review
const FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  emailAddress: 'Email Address',
  mobilePhoneNumber: 'Mobile Phone',
  otherPhoneNumber: 'Other Phone',
  buildingNameNumber: 'Building Name/Number',
  streetRoadBlock: 'Street',
  locality: 'Locality',
  cityTown: 'City/Town',
  stateProvinceRegion: 'County',
  postZipCode: 'Post Code',
  countryCode: 'Country',
  biography: 'Biography',
  summary: 'Summary',
  dateOfBirth: 'Date of Birth',
  gender: 'Gender',
  maritalStatus: 'Marital Status',
  nationality: 'Nationality',
  nationalInsuranceNumber: 'NI Number',
  isAgreedToMarketing: 'Marketing Consent',
  emergencyContactName: 'Emergency Contact',
  emergencyContactPhoneNumber: 'Emergency Phone',
  isUnspentConvictions: 'Unspent Convictions',
  convictionDetails: 'Conviction Details',
  payrollNumber: 'Payroll Number',
  defaultBranchId: 'Default Branch',
  defaultDeskId: 'Default Desk',
  workTypes: 'Work Types',
  geometry: 'Location',
  isExactGeo: 'Location Accuracy',
  availabilityRestriction: 'Availability Restriction'
};

export interface IWorkerWizardState {
  loading: boolean;
  //newWorkerOptions: NewWorkerOptionsDto;
  newWorker: NewWorkerViewModel;
  loadingDialog: boolean;
  cvImportMode: boolean;
  cvImportMarkVerified: boolean;  // Whether to mark positions/skills/qualifications as verified on CV import
  tempDocumentUris: string[];
  workTypeLocked: boolean;
  cvImportSummary: CVImportSummary | null;
  creationSucceeded: boolean | null;
  csvPopulatedFields: Set<string>;
  // Duplicate detection state
  duplicateCheckLoading: boolean;
  duplicateMatches: DuplicateWorkerMatch[];
  selectedDuplicateMatch: DuplicateWorkerMatch | null;
  mergeSelections: MergeSelections;
  // CV streaming state
  cvStream: CVStreamState;
}

// Standalone function to build merge field updates (can't be a class method due to vuex-module-decorators limitation)
function buildMergeFieldUpdates(
  newWorker: NewWorkerViewModel,
  mergeSelections: MergeSelections,
  existingWorkerAvailability: string[] = []
): MergeWorkerFieldUpdates {
  const updates = new MergeWorkerFieldUpdates();
  const worker = newWorker.worker;
  const choices = mergeSelections.fieldChoices;

  // Helper to set field if user chose 'new' value
  const setIfNew = <T>(field: string, value: T, setter: (v: T) => void) => {
    if (choices[field] === 'new') {
      setter(value);
    }
  };

  setIfNew('firstName', worker.firstName, v => updates.firstName = v);
  setIfNew('lastName', worker.lastName, v => updates.lastName = v);
  setIfNew('emailAddress', worker.emailAddress, v => updates.emailAddress = v);
  setIfNew('mobilePhoneNumber', worker.mobilePhoneNumber, v => updates.mobilePhoneNumber = v);
  setIfNew('otherPhoneNumber', worker.otherPhoneNumber, v => updates.otherPhoneNumber = v);
  setIfNew('buildingNameNumber', worker.buildingNameNumber, v => updates.buildingNameNumber = v);
  setIfNew('streetRoadBlock', worker.streetRoadBlock, v => updates.streetRoadBlock = v);
  setIfNew('locality', worker.locality, v => updates.locality = v);
  setIfNew('cityTown', worker.cityTown, v => updates.cityTown = v);
  setIfNew('stateProvinceRegion', worker.stateProvinceRegion, v => updates.stateProvinceRegion = v);
  setIfNew('postZipCode', worker.postZipCode, v => updates.postZipCode = v);
  setIfNew('countryCode', worker.countryCode, v => updates.countryCode = v);
  setIfNew('geometry', worker.geometry, v => updates.geometry = v);
  setIfNew('isExactGeo', worker.isExactGeo, v => updates.isExactGeo = v);
  setIfNew('biography', worker.biography, v => updates.biography = v);
  setIfNew('summary', worker.summary, v => updates.summary = v);
  setIfNew('dateOfBirth', worker.dateOfBirth, v => updates.dateOfBirth = v);
  setIfNew('gender', worker.gender, v => updates.gender = v);
  setIfNew('maritalStatus', worker.maritalStatus, v => updates.maritalStatus = v);
  setIfNew('nationality', worker.nationality, v => updates.nationality = v);
  setIfNew('nationalInsuranceNumber', worker.nationalInsuranceNumber, v => updates.nationalInsuranceNumber = v);
  setIfNew('workTypes', worker.workTypes, v => updates.workTypes = v);
  setIfNew('availabilityRestriction', worker.availabilityRestriction, v => updates.availabilityRestriction = v);
  setIfNew('isAgreedToMarketing', worker.isAgreedToMarketing, v => updates.isAgreedToMarketing = v);
  setIfNew('emergencyContactName', worker.emergencyContactName, v => updates.emergencyContactName = v);
  setIfNew('emergencyContactPhoneNumber', worker.emergencyContactPhoneNumber, v => updates.emergencyContactPhoneNumber = v);
  setIfNew('isUnspentConvictions', worker.isUnspentConvictions, v => updates.isUnspentConvictions = v);
  setIfNew('convictionDetails', worker.convictionDetails, v => updates.convictionDetails = v);
  setIfNew('payrollNumber', worker.payrollNumber, v => updates.payrollNumber = v);
  setIfNew('defaultBranchId', newWorker.defaultBranch?.id, v => updates.defaultBranchId = v);
  setIfNew('defaultDeskId', newWorker.defaultDesk?.id, v => updates.defaultDeskId = v);

  // Handle availability specially - merge existing (that aren't deleted) with new from wizard
  const wizardAvailability = new Set(worker.availability || []);
  const existingAvailabilitySet = new Set(existingWorkerAvailability || []);

  // Start with existing slots that are marked to keep (or exist in both)
  const mergedAvailability: string[] = [];
  existingWorkerAvailability.forEach(slot => {
    // Keep if: exists in both, OR exists only in existing and marked as 'keep'
    if (wizardAvailability.has(slot)) {
      // Exists in both - always keep
      mergedAvailability.push(slot);
    } else {
      // Exists only in existing - check if marked as keep
      const choice = mergeSelections.existingAvailabilityChoices[slot];
      if (choice !== 'delete') {
        mergedAvailability.push(slot);
      }
    }
  });

  // Add new slots from wizard (ones that don't exist in existing)
  (worker.availability || []).forEach(slot => {
    if (!existingAvailabilitySet.has(slot)) {
      mergedAvailability.push(slot);
    }
  });

  // Only send availability if there are changes
  const existingSorted = [...existingWorkerAvailability].sort();
  const mergedSorted = [...mergedAvailability].sort();
  if (JSON.stringify(existingSorted) !== JSON.stringify(mergedSorted)) {
    updates.availability = mergedAvailability;
  }

  return updates;
}

// Private module-level variable to store Firestore unsubscribe function
// Not part of Vuex state since it's a function reference
let cvStreamUnsubscribe: Unsubscribe | null = null;

@Module({ dynamic: true, store, name: 'WorkerWizard', namespaced: true})
class WorkerWizard extends VuexModule implements IWorkerWizardState {

  public loading = false;
  //public newWorkerOptions: NewWorkerOptionsDto = new NewWorkerOptionsDto();
  public newWorker: NewWorkerViewModel = new NewWorkerViewModel();
  public loadingDialog = false;
  public cvImportMode = false;
  public cvImportMarkVerified = false;
  public tempDocumentUris: string[] = [];
  public workTypeLocked = false;
  public cvImportSummary: CVImportSummary | null = null;
  public creationSucceeded: boolean | null = null;
  public csvPopulatedFields: Set<string> = new Set();
  // Duplicate detection state
  public duplicateCheckLoading = false;
  public duplicateMatches: DuplicateWorkerMatch[] = [];
  public selectedDuplicateMatch: DuplicateWorkerMatch | null = null;
  public mergeSelections: MergeSelections = {
    fieldChoices: {},
    positionFieldChoices: {},
    skillIsVerifiedChoices: {},
    qualificationIsVerifiedChoices: {},
    existingPositionChoices: {},
    existingSkillChoices: {},
    existingQualificationChoices: {},
    existingAvailabilityChoices: {}
  };
  // CV streaming state
  public cvStream: CVStreamState = {
    isStreaming: false,
    currentStep: 0,
    totalSteps: 6,
    stepMessage: '',
    thinkingContent: '',
    error: null
  };

  @Action
  public async createNewWorker(): Promise<boolean> {
    this.setLoading(true);
    this.context.commit("setCreationSucceeded", null);
    //fix for lat and long
    this.newWorker.worker.geometry.lat = Number(this.newWorker.worker.geometry.lat);
    this.newWorker.worker.geometry.lng = Number(this.newWorker.worker.geometry.lng);
    if (this.newWorker.defaultBranch) {
      this.newWorker.worker.defaultBranchId = this.newWorker.defaultBranch.id;
      //Only set the desk if we have a branch!
      if (this.newWorker.defaultDesk) {
        this.newWorker.worker.defaultDeskId = this.newWorker.defaultDesk.id;
      }
    }

    // Create a clean payload without the 'name' field in matched attributes
    // Also clear payRate for positions where isTemp is false
    const payload = {
      ...this.newWorker.worker,
      matchedSkills: this.newWorker.worker.matchedSkills?.map(({ name, ...rest }) => rest),
      matchedQualifications: this.newWorker.worker.matchedQualifications?.map(({ name, ...rest }) => rest),
      matchedPositions: this.newWorker.worker.matchedPositions?.map(({ name, ...rest }) => ({
        ...rest,
        payRate: rest.isTemp ? rest.payRate : 0
      }))
    };

    try {
      const response = await ajax.post(`/api/v1/agency/workers`, payload);
      this.context.commit("setCreateWorker", response.data);
      this.context.commit("setCreationSucceeded", true);
      this.setLoading(false);
      return true;
    } catch (error) {
      AppModule.handleApiError(error);
      this.context.commit("setCreationSucceeded", false);
      this.setLoading(false);
      return false;
    }
  }
  @Action
  async uploadWorkerCSV(formData: FormData){
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/workers/import-csv`, formData)
    .then(response => {
      this.context.commit("setWorkerFromCSVImport", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async uploadWorkerCV(formData: FormData){
    this.setLoadingDialog(true);
    try {
      const response = await ajax.post(`/api/v1/agency/workers/import-cv`, formData, {
        timeout: 120000
      });
      this.context.commit("setWorkerFromCVImport", response.data);
      this.setLoadingDialog(false);
    } catch (error: any) {
      // Check if this is an Axios error (has response or request) or a code error
      if (error.response || error.request || error.isAxiosError) {
        AppModule.handleApiError(error);
      } else {
        // This is a code error thrown during response processing
        console.error('[CV Import] Error processing response:', error);
        AppModule.updateErrorMessage(new ErrorMessage(
          'Error processing CV data: ' + (error.message || 'Unknown error'),
          'CV_PROCESSING_ERROR',
          false
        ));
      }
      this.setLoadingDialog(false);
    }
  }

  @Action
  async uploadWorkerCVWithStreaming(payload: { file: File; payRate: number; summaryFormat: string }): Promise<boolean> {
    // Reset streaming state and clean up any existing subscription
    if (cvStreamUnsubscribe) {
      cvStreamUnsubscribe();
      cvStreamUnsubscribe = null;
    }
    this.context.commit('resetCVStreamState');
    this.context.commit('setCVStreamIsStreaming', true);
    this.setLoadingDialog(true);

    // Return a Promise that resolves when parsing completes (success or error)
    return new Promise(async (resolve) => {
      try {
        // Start parsing and get unsubscribe function for Firestore listener
        cvStreamUnsubscribe = await startCVParseWithFirestore(
          payload.file,
          payload.payRate,
          payload.summaryFormat,
          {
            onProgress: (step, totalSteps, message) => {
              this.context.commit('setCVStreamProgress', { step, totalSteps, message });
            },
            onThinking: (thinking) => {
              this.context.commit('appendCVStreamThinking', thinking);
            },
            onResult: (result) => {
              this.context.commit('setWorkerFromCVImport', result);
              this.context.commit('setCVStreamIsStreaming', false);
              this.setLoadingDialog(false);
              resolve(true); // Success
            },
            onError: (error) => {
              this.context.commit('setCVStreamError', error);
              this.context.commit('setCVStreamIsStreaming', false);
              this.setLoadingDialog(false);
              AppModule.updateErrorMessage(new ErrorMessage(
                'CV Import Error: ' + error,
                'CV_STREAM_ERROR',
                false
              ));
              resolve(false); // Error
            },
            onClose: () => {
              // Firestore subscription closed - streaming complete
              console.log('[CV Import Stream] Firestore subscription closed');
              if (cvStreamUnsubscribe) {
                cvStreamUnsubscribe();
                cvStreamUnsubscribe = null;
              }
            }
          }
        );
      } catch (error: any) {
        console.error('[CV Import Stream] Error:', error);
        this.context.commit('setCVStreamError', error.message || 'Unknown error');
        this.context.commit('setCVStreamIsStreaming', false);
        this.setLoadingDialog(false);
        AppModule.updateErrorMessage(new ErrorMessage(
          'CV Import Error: ' + (error.message || 'Unknown error'),
          'CV_STREAM_ERROR',
          false
        ));
        resolve(false); // Error
      }
    });
  }

  @Action
  cancelCVStreaming() {
    // Cancel Firestore subscription if active
    if (cvStreamUnsubscribe) {
      console.log('[CV Import Stream] Cancelling Firestore subscription');
      cvStreamUnsubscribe();
      cvStreamUnsubscribe = null;
    }
    this.context.commit('setCVStreamIsStreaming', false);
    this.setLoadingDialog(false);
  }

  @Action
  cleanState() {
    // Clean up Firestore subscription if active
    if (cvStreamUnsubscribe) {
      cvStreamUnsubscribe();
      cvStreamUnsubscribe = null;
    }
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.loadingDialog = false;
    this.newWorker = new NewWorkerViewModel();
    this.cvImportMode = false;
    this.cvImportMarkVerified = false;
    this.tempDocumentUris = [];
    this.workTypeLocked = false;
    this.cvImportSummary = null;
    this.creationSucceeded = null;
    this.csvPopulatedFields = new Set();
    // Reset duplicate detection state
    this.duplicateCheckLoading = false;
    this.duplicateMatches = [];
    this.selectedDuplicateMatch = null;
    this.mergeSelections = {
      fieldChoices: {},
      positionFieldChoices: {},
      skillIsVerifiedChoices: {},
      qualificationIsVerifiedChoices: {},
      existingPositionChoices: {},
      existingSkillChoices: {},
      existingQualificationChoices: {},
      existingAvailabilityChoices: {}
    };
    // Reset CV streaming state
    this.cvStream = {
      isStreaming: false,
      currentStep: 0,
      totalSteps: 6,
      stepMessage: '',
      thinkingContent: '',
      error: null
    };
  }

  // CV Streaming Mutations
  @Mutation
  resetCVStreamState() {
    this.cvStream = {
      isStreaming: false,
      currentStep: 0,
      totalSteps: 6,
      stepMessage: '',
      thinkingContent: '',
      error: null
    };
  }

  @Mutation
  setCVStreamIsStreaming(isStreaming: boolean) {
    this.cvStream.isStreaming = isStreaming;
  }

  @Mutation
  setCVStreamProgress(payload: { step: number; totalSteps: number; message: string }) {
    this.cvStream.currentStep = payload.step;
    this.cvStream.totalSteps = payload.totalSteps;
    this.cvStream.stepMessage = payload.message;
  }

  @Mutation
  appendCVStreamThinking(thinking: string) {
    this.cvStream.thinkingContent += thinking;
  }

  @Mutation
  setCVStreamError(error: string) {
    this.cvStream.error = error;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }

  @Mutation
  setCvImportMarkVerified(value: boolean) {
      this.cvImportMarkVerified = value;
  }

  @Mutation
  private async setCreateWorker(worker: WorkerDto) {
    this.newWorker.id = worker.id;
  }
    
    
  @Mutation
  private async setWorkerFromCSVImport(worker: CreateWorkerRequest) {
    // Track which fields are populated by CSV import
    this.csvPopulatedFields = new Set();

    // Helper to set field and track if it has a value
    const setField = (field: string, value: any) => {
      if (value !== undefined && value !== null && value !== '') {
        this.csvPopulatedFields.add(field);
      }
    };

    // Personal info
    setField('firstName', worker.firstName);
    setField('lastName', worker.lastName);
    setField('emailAddress', worker.emailAddress);
    setField('mobilePhoneNumber', worker.mobilePhoneNumber);
    setField('gender', worker.gender);

    // Address
    setField('buildingNameNumber', worker.buildingNameNumber);
    setField('streetRoadBlock', worker.streetRoadBlock);
    setField('locality', worker.locality);
    setField('cityTown', worker.cityTown);
    setField('stateProvinceRegion', worker.stateProvinceRegion);
    setField('postZipCode', worker.postZipCode);
    setField('countryCode', worker.countryCode);

    // Geometry
    if (worker.geometry && (worker.geometry.lat !== 0 || worker.geometry.lng !== 0)) {
      setField('geometry', true);
    }

    // Summary
    setField('summary', worker.summary);

    // Set the worker data
    this.newWorker.worker = worker;
  }

  @Mutation
  private setWorkerFromCVImport(result: CVParseResult) {
    this.cvImportMode = true;

    // Helper to check if field was populated by CSV (CSV takes priority)
    const canSet = (field: string) => !this.csvPopulatedFields.has(field);

    // Populate personal info (only if not already set by CSV)
    // Normalize names: convert ALL CAPS to Title Case, but preserve mixed case like "McDonald"
    if (canSet('firstName')) {
      this.newWorker.worker.firstName = normalizeNameFromCV(result.personalInfo.firstName);
    }
    if (canSet('lastName')) {
      this.newWorker.worker.lastName = normalizeNameFromCV(result.personalInfo.lastName);
    }
    if (canSet('emailAddress')) {
      this.newWorker.worker.emailAddress = result.personalInfo.email;
    }
    if (canSet('mobilePhoneNumber')) {
      this.newWorker.worker.mobilePhoneNumber = result.personalInfo.mobile;
    }
    if (canSet('gender')) {
      this.newWorker.worker.gender = result.personalInfo.gender;
    }

    // Populate address (only if not already set by CSV)
    if (canSet('buildingNameNumber')) {
      this.newWorker.worker.buildingNameNumber = result.personalInfo.address.buildingNameNumber;
    }
    if (canSet('streetRoadBlock')) {
      this.newWorker.worker.streetRoadBlock = result.personalInfo.address.streetRoadBlock;
    }
    if (canSet('locality')) {
      this.newWorker.worker.locality = result.personalInfo.address.locality;
    }
    if (canSet('cityTown')) {
      this.newWorker.worker.cityTown = result.personalInfo.address.cityTown;
    }
    if (canSet('stateProvinceRegion')) {
      this.newWorker.worker.stateProvinceRegion = result.personalInfo.address.stateProvinceRegion;
    }
    if (canSet('postZipCode')) {
      this.newWorker.worker.postZipCode = result.personalInfo.address.postZipCode;
    }
    if (canSet('countryCode')) {
      this.newWorker.worker.countryCode = result.personalInfo.address.countryCode;
    }

    // Populate geometry from CV parse geocoding result (only if not already set by CSV)
    if (canSet('geometry')) {
      this.newWorker.worker.geometry.lat = result.personalInfo.address.geometry?.lat || 0;
      this.newWorker.worker.geometry.lng = result.personalInfo.address.geometry?.lng || 0;
      this.newWorker.worker.isExactGeo = result.personalInfo.address.isExactGeo ?? false;
    }

    // Populate professional overview (only if not already set by CSV)
    if (canSet('summary')) {
      this.newWorker.worker.summary = result.professionalInfo.overview;
    }

    // Keep the existing work types - they were already selected before CV upload
    const hasTemp = this.newWorker.worker.workTypes?.includes(WorkType.Temp) || false;
    const hasPerm = this.newWorker.worker.workTypes?.includes(WorkType.Perm) || false;

    // Populate attributes from CV (CSV doesn't include these, so no merge needed)
    // Use the cvImportMarkVerified setting for isVerified (defaults to false)
    const markVerified = this.cvImportMarkVerified;
    const matchedSkills = result.matchedSkills || [];
    const matchedQualifications = result.matchedQualifications || [];
    const matchedPositions = result.matchedPositions || [];

    this.newWorker.worker.matchedSkills = matchedSkills.map(s => ({
      attributeId: s.attributeId,
      name: s.name,
      source: s.source,
      agentMatchReason: s.agentMatchReason,
      agentMatchConfidence: s.agentMatchConfidence,
      isVerified: markVerified
    }));
    this.newWorker.worker.matchedQualifications = matchedQualifications.map(q => ({
      attributeId: q.attributeId,
      name: q.name,
      source: q.source,
      agentMatchReason: q.agentMatchReason,
      agentMatchConfidence: q.agentMatchConfidence,
      isVerified: markVerified
    }));
    this.newWorker.worker.matchedPositions = matchedPositions.map(p => ({
      attributeId: p.attributeId,
      name: p.name,
      source: p.source,
      agentMatchReason: p.agentMatchReason,
      agentMatchConfidence: p.agentMatchConfidence,
      isVerified: markVerified,
      isTemp: hasTemp,
      isPerm: hasPerm,
      payRate: hasTemp ? result.payRate : 0
    }));

    // Store temp document URIs (CV file uploaded during parsing)
    this.tempDocumentUris = [result.tempDocumentUri];
    this.newWorker.worker.tempDocumentUris = [result.tempDocumentUri];

    // Store agentic task ID so we can link it when creating the worker
    this.newWorker.worker.agenticTaskId = result.agenticTaskId;

    // Set CV import summary for display on the work type page
    this.cvImportSummary = {
      positionCount: matchedPositions.length,
      skillCount: matchedSkills.length,
      qualificationCount: matchedQualifications.length
    };
  }

  @Mutation
  lockWorkType() {
    this.workTypeLocked = true;
  }

  @Mutation
  setCreationSucceeded(succeeded: boolean | null) {
    this.creationSucceeded = succeeded;
  }

  @Mutation
  addManualSkill(skill: { skillId: string; name: string }) {
    if (!this.newWorker.worker.matchedSkills) {
      this.newWorker.worker.matchedSkills = [];
    }
    this.newWorker.worker.matchedSkills.push({
      attributeId: skill.skillId,
      name: skill.name,
      source: 1, // MANUAL_AGENCY
      isVerified: true
    });
  }

  @Mutation
  addManualQualification(qualification: { qualificationId: string; name: string }) {
    if (!this.newWorker.worker.matchedQualifications) {
      this.newWorker.worker.matchedQualifications = [];
    }
    this.newWorker.worker.matchedQualifications.push({
      attributeId: qualification.qualificationId,
      name: qualification.name,
      source: 1, // MANUAL_AGENCY
      isVerified: true
    });
  }

  @Mutation
  addManualPosition(position: { positionId: string; name: string; payRate: number }) {
    if (!this.newWorker.worker.matchedPositions) {
      this.newWorker.worker.matchedPositions = [];
    }
    const hasTemp = this.newWorker.worker.workTypes?.includes(WorkType.Temp) || false;
    const hasPerm = this.newWorker.worker.workTypes?.includes(WorkType.Perm) || false;
    this.newWorker.worker.matchedPositions.push({
      attributeId: position.positionId,
      name: position.name,
      source: 1, // MANUAL_AGENCY
      isVerified: true,
      isTemp: hasTemp,
      isPerm: hasPerm,
      payRate: hasTemp ? position.payRate : 0
    });
  }

  @Mutation
  removeSkill(skillId: string) {
    if (this.newWorker.worker.matchedSkills) {
      this.newWorker.worker.matchedSkills = this.newWorker.worker.matchedSkills.filter(
        s => s.attributeId !== skillId
      );
    }
  }

  @Mutation
  removeQualification(qualificationId: string) {
    if (this.newWorker.worker.matchedQualifications) {
      this.newWorker.worker.matchedQualifications = this.newWorker.worker.matchedQualifications.filter(
        q => q.attributeId !== qualificationId
      );
    }
  }

  @Mutation
  removePosition(positionId: string) {
    if (this.newWorker.worker.matchedPositions) {
      this.newWorker.worker.matchedPositions = this.newWorker.worker.matchedPositions.filter(
        p => p.attributeId !== positionId
      );
    }
  }

  // ============================================
  // Getters for Merge Review UI
  // ============================================

  get fieldComparisons(): FieldComparison[] {
    if (!this.selectedDuplicateMatch) return [];

    const newWorker = this.newWorker.worker;
    const existingWorker = this.selectedDuplicateMatch.worker;
    const existingPerson = existingWorker.person || {};

    const getExistingValue = (field: string): any => {
      // Only firstName and lastName are on the Person entity, everything else is on Worker
      const personFields = ['firstName', 'lastName'];
      if (personFields.includes(field)) {
        return (existingPerson as any)[field];
      }
      return (existingWorker as any)[field];
    };

    const getNewValue = (field: string): any => {
      if (field === 'defaultBranchId') return this.newWorker.defaultBranch?.id;
      if (field === 'defaultDeskId') return this.newWorker.defaultDesk?.id;
      return (newWorker as any)[field];
    };

    const formatDisplayValue = (field: string, value: any): string => {
      if (value === null || value === undefined) return '-';
      if (value === '') return '-';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';

      // Handle gender enum
      if (field === 'gender') {
        const genderMap: Record<number, string> = { 0: 'Unknown', 1: 'Male', 2: 'Female' };
        return genderMap[value] || String(value);
      }

      // Handle marital status enum
      if (field === 'maritalStatus') {
        const statusMap: Record<number, string> = { 0: 'Unknown', 1: 'Single', 2: 'Married', 3: 'Divorced', 4: 'Widowed', 5: 'Other' };
        return statusMap[value] || String(value);
      }

      // Handle date
      if (field === 'dateOfBirth' && value) {
        try {
          const date = new Date(value);
          return date.toLocaleDateString('en-GB');
        } catch {
          return String(value);
        }
      }

      // Handle work types array
      if (field === 'workTypes' && Array.isArray(value)) {
        return value.join(', ') || '-';
      }

      // Handle geometry
      if (field === 'geometry' && value) {
        if (value.lat === 0 && value.lng === 0) return '-';
        return `${value.lat}, ${value.lng}`;
      }

      // Handle isExactGeo
      if (field === 'isExactGeo') {
        return value === true ? 'Exact' : 'Approximate';
      }

      // Handle availability restriction enum
      if (field === 'availabilityRestriction') {
        const restrictionMap: Record<number, string> = { 0: 'None', 1: 'No Nights', 2: 'No Weekends', 3: 'No Nights or Weekends' };
        return restrictionMap[value] || String(value);
      }

      // Handle default branch - show name instead of ID
      // Note: Backend Branch struct has brandName/registeredName, not 'name'
      if (field === 'defaultBranchId') {
        if (!value) return '-';
        const valueStr = String(value);
        // Check if this is the new worker's branch (frontend uses 'name' property)
        if (this.newWorker.defaultBranch && String(this.newWorker.defaultBranch.id) === valueStr) {
          return this.newWorker.defaultBranch.name || '-';
        }
        // Check if this is the existing worker's branch (backend uses 'brandName')
        if (existingWorker.defaultBranch) {
          return existingWorker.defaultBranch.brandName || existingWorker.defaultBranch.registeredName || '-';
        }
        return '-';
      }

      // Handle default desk - show name instead of ID
      if (field === 'defaultDeskId') {
        if (!value) return '-';
        const valueStr = String(value);
        // Check if this is the new worker's desk
        if (this.newWorker.defaultDesk && String(this.newWorker.defaultDesk.id) === valueStr) {
          return this.newWorker.defaultDesk.name || '-';
        }
        // Check if this is the existing worker's desk
        if (existingWorker.defaultDesk && existingWorker.defaultDesk.name) {
          return existingWorker.defaultDesk.name;
        }
        // Debug fallback - show ID if we can't resolve name
        return 'Desk: ' + valueStr.substring(0, 8) + '...';
      }

      return String(value);
    };

    const fieldsToCompare = [
      'firstName', 'lastName', 'emailAddress', 'mobilePhoneNumber', 'otherPhoneNumber',
      'buildingNameNumber', 'streetRoadBlock', 'locality', 'cityTown', 'stateProvinceRegion',
      'postZipCode', 'countryCode', 'dateOfBirth', 'gender', 'maritalStatus', 'nationality',
      'nationalInsuranceNumber', 'emergencyContactName', 'emergencyContactPhoneNumber',
      'isAgreedToMarketing', 'isUnspentConvictions', 'convictionDetails',
      'workTypes', 'geometry', 'isExactGeo', 'summary', 'biography', 'availabilityRestriction',
      'payrollNumber', 'defaultBranchId', 'defaultDeskId'
    ];

    return fieldsToCompare.map(field => {
      const newValue = getNewValue(field);
      const existingValue = getExistingValue(field);
      const isDifferent = !areValuesEqual(newValue, existingValue);

      return {
        field,
        label: FIELD_LABELS[field] || field,
        newValue,
        existingValue,
        newDisplayValue: formatDisplayValue(field, newValue),
        existingDisplayValue: formatDisplayValue(field, existingValue),
        choice: this.mergeSelections.fieldChoices[field] || 'new',
        isDifferent
      };
    });
  }

  get fieldsWithDifferences(): FieldComparison[] {
    return this.fieldComparisons.filter(f => f.isDifferent);
  }

  get identicalFields(): FieldComparison[] {
    return this.fieldComparisons.filter(f => !f.isDifferent);
  }

  // ============================================
  // Position Comparisons for Merge Review
  // ============================================

  // Positions that exist in both wizard and existing worker - need field-level merge decisions
  get positionsInBoth(): PositionMergeComparison[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingPositions = this.selectedDuplicateMatch.positions || [];
    const wizardPositions = this.newWorker.worker.matchedPositions || [];

    const existingPosMap = new Map<string, any>();
    existingPositions.forEach(p => {
      existingPosMap.set(p.positionId, p);
    });

    // Helper to check if workTypes array contains a specific type
    const hasWorkType = (workTypes: any[] | undefined, type: string): boolean => {
      if (!workTypes || !Array.isArray(workTypes)) return false;
      return workTypes.some(wt => wt === type || wt.toString() === type);
    };

    const comparisons: PositionMergeComparison[] = [];

    wizardPositions.forEach(newPos => {
      const existingPos = existingPosMap.get(newPos.attributeId);
      if (existingPos) {
        const defaultChoices = this.mergeSelections.positionFieldChoices[newPos.attributeId] || {
          workTypes: 'new',
          source: 'new',
          payRate: 'new',
          isVerified: 'new'
        };

        // Existing positions use workTypes array, convert to booleans
        const existingIsTemp = hasWorkType(existingPos.workTypes, 'Temp');
        const existingIsPerm = hasWorkType(existingPos.workTypes, 'Perm');

        comparisons.push({
          attributeId: newPos.attributeId,
          name: newPos.name || (existingPos.position && existingPos.position.name) || newPos.attributeId,
          newPosition: newPos,
          existingPosition: existingPos,
          choices: defaultChoices,
          newWorkTypes: { isTemp: newPos.isTemp || false, isPerm: newPos.isPerm || false },
          existingWorkTypes: { isTemp: existingIsTemp, isPerm: existingIsPerm },
          newPayRate: newPos.payRate || 0,
          existingPayRate: existingPos.payRate || 0,
          newIsVerified: newPos.isVerified || false,
          existingIsVerified: existingPos.isVerified || false,
          hasNewSource: !!newPos.source || !!newPos.agentMatchReason
        });
      }
    });

    return comparisons;
  }

  // Positions that exist only on existing worker - need Keep/Delete decision
  get positionsExistingOnly(): Array<{ position: any; choice: KeepDeleteChoice }> {
    if (!this.selectedDuplicateMatch) return [];

    const wizardPosIds = new Set((this.newWorker.worker.matchedPositions || []).map(p => p.attributeId));
    const existingOnly = (this.selectedDuplicateMatch.positions || []).filter(p => !wizardPosIds.has(p.positionId));

    return existingOnly.map(pos => ({
      position: pos,
      choice: this.mergeSelections.existingPositionChoices[pos.positionId] || 'keep'
    }));
  }

  // Positions that exist only in wizard - will be added (no decision needed)
  get positionsNewOnly(): any[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingPosIds = new Set((this.selectedDuplicateMatch.positions || []).map(p => p.positionId));
    return (this.newWorker.worker.matchedPositions || []).filter(p => !existingPosIds.has(p.attributeId));
  }

  // Skills that exist only on existing worker - need Keep/Delete decision
  get skillsExistingOnly(): Array<{ skill: any; choice: KeepDeleteChoice }> {
    if (!this.selectedDuplicateMatch) return [];

    const wizardSkillIds = new Set((this.newWorker.worker.matchedSkills || []).map(s => s.attributeId));
    const existingOnly = (this.selectedDuplicateMatch.skills || []).filter(s => !wizardSkillIds.has(s.skillId));

    return existingOnly.map(skill => ({
      skill,
      choice: this.mergeSelections.existingSkillChoices[skill.skillId] || 'keep'
    }));
  }

  // Skills that exist only in wizard - will be added (no decision needed)
  get skillsNewOnly(): any[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingSkillIds = new Set((this.selectedDuplicateMatch.skills || []).map(s => s.skillId));
    return (this.newWorker.worker.matchedSkills || []).filter(s => !existingSkillIds.has(s.attributeId));
  }

  // Skills that exist in both - need isVerified decision
  get skillsInBoth(): SkillMergeComparison[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingSkills = this.selectedDuplicateMatch.skills || [];
    const wizardSkills = this.newWorker.worker.matchedSkills || [];

    const existingSkillMap = new Map<string, any>();
    existingSkills.forEach(s => {
      existingSkillMap.set(s.skillId, s);
    });

    const comparisons: SkillMergeComparison[] = [];

    wizardSkills.forEach(newSkill => {
      const existingSkill = existingSkillMap.get(newSkill.attributeId);
      if (existingSkill) {
        comparisons.push({
          attributeId: newSkill.attributeId,
          name: newSkill.name || (existingSkill.skill && existingSkill.skill.name) || newSkill.attributeId,
          newSkill: newSkill,
          existingSkill: existingSkill,
          newIsVerified: newSkill.isVerified || false,
          existingIsVerified: existingSkill.isVerified || false,
          isVerifiedChoice: this.mergeSelections.skillIsVerifiedChoices[newSkill.attributeId] || 'new'
        });
      }
    });

    return comparisons;
  }

  // Qualifications that exist only on existing worker - need Keep/Delete decision
  get qualificationsExistingOnly(): Array<{ qualification: any; choice: KeepDeleteChoice }> {
    if (!this.selectedDuplicateMatch) return [];

    const wizardQualIds = new Set((this.newWorker.worker.matchedQualifications || []).map(q => q.attributeId));
    const existingOnly = (this.selectedDuplicateMatch.qualifications || []).filter(q => !wizardQualIds.has(q.qualificationId));

    return existingOnly.map(qual => ({
      qualification: qual,
      choice: this.mergeSelections.existingQualificationChoices[qual.qualificationId] || 'keep'
    }));
  }

  // Qualifications that exist only in wizard - will be added (no decision needed)
  get qualificationsNewOnly(): any[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingQualIds = new Set((this.selectedDuplicateMatch.qualifications || []).map(q => q.qualificationId));
    return (this.newWorker.worker.matchedQualifications || []).filter(q => !existingQualIds.has(q.attributeId));
  }

  // Qualifications that exist in both - need isVerified decision
  get qualificationsInBoth(): QualificationMergeComparison[] {
    if (!this.selectedDuplicateMatch) return [];

    const existingQualifications = this.selectedDuplicateMatch.qualifications || [];
    const wizardQualifications = this.newWorker.worker.matchedQualifications || [];

    const existingQualMap = new Map<string, any>();
    existingQualifications.forEach(q => {
      existingQualMap.set(q.qualificationId, q);
    });

    const comparisons: QualificationMergeComparison[] = [];

    wizardQualifications.forEach(newQual => {
      const existingQual = existingQualMap.get(newQual.attributeId);
      if (existingQual) {
        comparisons.push({
          attributeId: newQual.attributeId,
          name: newQual.name || (existingQual.qualification && existingQual.qualification.name) || newQual.attributeId,
          newQualification: newQual,
          existingQualification: existingQual,
          newIsVerified: newQual.isVerified || false,
          existingIsVerified: existingQual.isVerified || false,
          isVerifiedChoice: this.mergeSelections.qualificationIsVerifiedChoices[newQual.attributeId] || 'new'
        });
      }
    });

    return comparisons;
  }

  // ============================================
  // Availability Comparisons for Merge Review
  // ============================================

  // Availability slots that exist only in existing worker - user can choose to keep or delete
  get availabilityExistingOnly(): Array<{ slot: string; displayName: string; choice: KeepDeleteChoice }> {
    if (!this.selectedDuplicateMatch) return [];

    const wizardAvailability = new Set(this.newWorker.worker.availability || []);
    const existingAvailability = this.selectedDuplicateMatch.worker.availability || [];

    // Map slot keys to display names
    const slotDisplayNames: Record<string, string> = {
      'MonAM': 'Monday AM', 'MonPM': 'Monday PM',
      'TueAM': 'Tuesday AM', 'TuePM': 'Tuesday PM',
      'WedAM': 'Wednesday AM', 'WedPM': 'Wednesday PM',
      'ThuAM': 'Thursday AM', 'ThuPM': 'Thursday PM',
      'FriAM': 'Friday AM', 'FriPM': 'Friday PM',
      'SatAM': 'Saturday AM', 'SatPM': 'Saturday PM',
      'SunAM': 'Sunday AM', 'SunPM': 'Sunday PM'
    };

    const existingOnly = existingAvailability.filter((slot: string) => !wizardAvailability.has(slot));

    return existingOnly.map((slot: string) => ({
      slot,
      displayName: slotDisplayNames[slot] || slot,
      choice: this.mergeSelections.existingAvailabilityChoices[slot] || 'keep'
    }));
  }

  // Availability slots that exist only in wizard - will be added (no decision needed)
  get availabilityNewOnly(): Array<{ slot: string; displayName: string }> {
    if (!this.selectedDuplicateMatch) return [];

    const existingAvailability = new Set(this.selectedDuplicateMatch.worker.availability || []);
    const wizardAvailability = this.newWorker.worker.availability || [];

    // Map slot keys to display names
    const slotDisplayNames: Record<string, string> = {
      'MonAM': 'Monday AM', 'MonPM': 'Monday PM',
      'TueAM': 'Tuesday AM', 'TuePM': 'Tuesday PM',
      'WedAM': 'Wednesday AM', 'WedPM': 'Wednesday PM',
      'ThuAM': 'Thursday AM', 'ThuPM': 'Thursday PM',
      'FriAM': 'Friday AM', 'FriPM': 'Friday PM',
      'SatAM': 'Saturday AM', 'SatPM': 'Saturday PM',
      'SunAM': 'Sunday AM', 'SunPM': 'Sunday PM'
    };

    const newOnly = wizardAvailability.filter(slot => !existingAvailability.has(slot));

    return newOnly.map(slot => ({
      slot,
      displayName: slotDisplayNames[slot] || slot
    }));
  }

  // Availability slots that exist in both - no decision needed, just show as matching
  get availabilityInBoth(): Array<{ slot: string; displayName: string }> {
    if (!this.selectedDuplicateMatch) return [];

    const existingAvailability = new Set(this.selectedDuplicateMatch.worker.availability || []);
    const wizardAvailability = this.newWorker.worker.availability || [];

    // Map slot keys to display names
    const slotDisplayNames: Record<string, string> = {
      'MonAM': 'Monday AM', 'MonPM': 'Monday PM',
      'TueAM': 'Tuesday AM', 'TuePM': 'Tuesday PM',
      'WedAM': 'Wednesday AM', 'WedPM': 'Wednesday PM',
      'ThuAM': 'Thursday AM', 'ThuPM': 'Thursday PM',
      'FriAM': 'Friday AM', 'FriPM': 'Friday PM',
      'SatAM': 'Saturday AM', 'SatPM': 'Saturday PM',
      'SunAM': 'Sunday AM', 'SunPM': 'Sunday PM'
    };

    const inBoth = wizardAvailability.filter(slot => existingAvailability.has(slot));

    return inBoth.map(slot => ({
      slot,
      displayName: slotDisplayNames[slot] || slot
    }));
  }

  // ============================================
  // Duplicate Detection and Merge Actions
  // ============================================

  @Action
  public async checkForDuplicates(): Promise<boolean> {
    this.context.commit('setDuplicateCheckLoading', true);

    const request = new CheckWorkerDuplicatesRequest();
    request.emailAddress = this.newWorker.worker.emailAddress;
    request.firstName = this.newWorker.worker.firstName;
    request.lastName = this.newWorker.worker.lastName;

    try {
      const response = await ajax.post<CheckWorkerDuplicatesResponse>(
        '/api/v1/agency/workers/check-duplicates',
        request
      );
      this.context.commit('setDuplicateMatches', response.data.matches || []);
      this.context.commit('setDuplicateCheckLoading', false);
      return response.data.hasDuplicates;
    } catch (error) {
      AppModule.handleApiError(error);
      this.context.commit('setDuplicateCheckLoading', false);
      return false;
    }
  }

  @Action
  public async mergeIntoExistingWorker(): Promise<boolean> {
    if (!this.selectedDuplicateMatch) {
      console.error('[WorkerWizard] No selected duplicate match');
      return false;
    }

    const workerId = this.selectedDuplicateMatch.worker.id;
    if (!workerId) {
      console.error('[WorkerWizard] Selected duplicate match has no worker ID');
      return false;
    }

    this.setLoading(true);
    this.context.commit('setCreationSucceeded', null);

    // Build the merge request based on user's selections
    const request = new MergeWorkerRequest();
    const existingWorkerAvailability = this.selectedDuplicateMatch.worker.availability || [];
    request.updateFields = buildMergeFieldUpdates(this.newWorker, this.mergeSelections, existingWorkerAvailability);

    // Get existing attribute IDs for comparison
    const existingSkillIds = new Set((this.selectedDuplicateMatch.skills || []).map(s => s.skillId));
    const existingQualIds = new Set((this.selectedDuplicateMatch.qualifications || []).map(q => q.qualificationId));
    const existingPosIds = new Set((this.selectedDuplicateMatch.positions || []).map(p => p.positionId));

    // Skills: Add new ones (not on existing worker)
    request.addSkills = (this.newWorker.worker.matchedSkills || [])
      .filter(s => !existingSkillIds.has(s.attributeId))
      .map(({ name, ...rest }): MatchedAttributeForMerge => rest);

    // Skills: Delete existing ones marked for deletion
    request.deleteSkillIds = Object.entries(this.mergeSelections.existingSkillChoices)
      .filter(([, choice]) => choice === 'delete')
      .map(([skillId]) => skillId);

    // Qualifications: Add new ones (not on existing worker)
    request.addQualifications = (this.newWorker.worker.matchedQualifications || [])
      .filter(q => !existingQualIds.has(q.attributeId))
      .map(({ name, ...rest }): MatchedAttributeForMerge => rest);

    // Qualifications: Delete existing ones marked for deletion
    request.deleteQualificationIds = Object.entries(this.mergeSelections.existingQualificationChoices)
      .filter(([, choice]) => choice === 'delete')
      .map(([qualificationId]) => qualificationId);

    // Positions: Add new ones (not on existing worker)
    request.addPositions = (this.newWorker.worker.matchedPositions || [])
      .filter(p => !existingPosIds.has(p.attributeId))
      .map(({ name, ...rest }): MatchedAttributeForMerge => ({
        ...rest,
        payRate: rest.isTemp ? rest.payRate : 0
      }));

    // Positions: Delete existing ones marked for deletion
    request.deletePositionIds = Object.entries(this.mergeSelections.existingPositionChoices)
      .filter(([, choice]) => choice === 'delete')
      .map(([positionId]) => positionId);

    // Positions: Update ones that exist in both (with field-level choices)
    request.updatePositions = (this.newWorker.worker.matchedPositions || [])
      .filter(p => existingPosIds.has(p.attributeId))
      .map(newPos => {
        const choices = this.mergeSelections.positionFieldChoices[newPos.attributeId] || {
          workTypes: 'existing',
          source: 'existing',
          payRate: 'existing',
          isVerified: 'existing'
        };

        const update = new UpdateWorkerPositionForMerge();
        update.positionId = newPos.attributeId;

        // Work types
        update.updateWorkTypes = choices.workTypes === 'new';
        if (update.updateWorkTypes) {
          update.isTemp = newPos.isTemp;
          update.isPerm = newPos.isPerm;
        }

        // Source (includes agentMatchReason, agentMatchConfidence)
        update.updateSource = choices.source === 'new';
        if (update.updateSource) {
          update.source = newPos.source;
          update.agentMatchReason = newPos.agentMatchReason;
          update.agentMatchConfidence = newPos.agentMatchConfidence;
        }

        // Pay rate
        update.updatePayRate = choices.payRate === 'new';
        if (update.updatePayRate) {
          update.payRate = newPos.isTemp ? newPos.payRate : 0;
        }

        // Verified status
        update.updateIsVerified = choices.isVerified === 'new';
        if (update.updateIsVerified) {
          update.isVerified = newPos.isVerified || false;
        }

        return update;
      });

    // Include temp documents and agentic task ID
    request.tempDocumentUris = this.tempDocumentUris;
    request.agenticTaskId = this.newWorker.worker.agenticTaskId;

    console.log('[WorkerWizard] Merging into worker:', workerId, 'with request:', request);

    try {
      const response = await ajax.post(
        `/api/v1/agency/workers/${workerId}/merge`,
        request
      );
      console.log('[WorkerWizard] Merge successful:', response.data);
      this.context.commit('setCreateWorker', response.data);
      this.context.commit('setCreationSucceeded', true);
      this.setLoading(false);
      return true;
    } catch (error) {
      console.error('[WorkerWizard] Merge failed:', error);
      AppModule.handleApiError(error);
      this.context.commit('setCreationSucceeded', false);
      this.setLoading(false);
      return false;
    }
  }

  @Action
  selectDuplicateMatch(match: DuplicateWorkerMatch | null) {
    this.context.commit('setSelectedDuplicateMatch', match);
    if (match) {
      // Initialize merge selections - default all wizard data to 'new' (user entered this data deliberately)
      this.context.commit('initializeMergeSelections', match);
    }
  }

  @Action
  clearDuplicateSelection() {
    this.context.commit('setSelectedDuplicateMatch', null);
    this.context.commit('clearMergeSelections');
  }

  @Action
  resetDuplicateCheck() {
    this.context.commit('setDuplicateMatches', []);
    this.context.commit('setSelectedDuplicateMatch', null);
    this.context.commit('clearMergeSelections');
  }

  // ============================================
  // Duplicate Detection Mutations
  // ============================================

  @Mutation
  setDuplicateCheckLoading(loading: boolean) {
    this.duplicateCheckLoading = loading;
  }

  @Mutation
  setDuplicateMatches(matches: DuplicateWorkerMatch[]) {
    this.duplicateMatches = matches;
  }

  @Mutation
  setSelectedDuplicateMatch(match: DuplicateWorkerMatch | null) {
    this.selectedDuplicateMatch = match;
  }

  @Mutation
  initializeMergeSelections(match: DuplicateWorkerMatch) {
    const fieldChoices: Record<string, FieldChoice> = {};
    const newWorker = this.newWorker.worker;
    const existingWorker = match.worker;
    const existingPerson = existingWorker.person || {};

    // Helper to get value from existing worker (handles person nested object)
    const getExistingValue = (field: string): any => {
      // Only firstName and lastName are on the Person entity, everything else is on Worker
      const personFields = ['firstName', 'lastName'];
      if (personFields.includes(field)) {
        return (existingPerson as any)[field];
      }
      // Worker-level fields
      return (existingWorker as any)[field];
    };

    // Helper to get value from new worker
    const getNewValue = (field: string): any => {
      if (field === 'defaultBranchId') return this.newWorker.defaultBranch?.id;
      if (field === 'defaultDeskId') return this.newWorker.defaultDesk?.id;
      return (newWorker as any)[field];
    };

    // Apply smart defaults for each field
    const fieldsToCompare = [
      'firstName', 'lastName', 'emailAddress', 'mobilePhoneNumber', 'otherPhoneNumber',
      'buildingNameNumber', 'streetRoadBlock', 'locality', 'cityTown', 'stateProvinceRegion',
      'postZipCode', 'countryCode', 'biography', 'summary', 'dateOfBirth', 'gender',
      'maritalStatus', 'nationality', 'nationalInsuranceNumber',
      'isAgreedToMarketing', 'emergencyContactName', 'emergencyContactPhoneNumber',
      'isUnspentConvictions', 'convictionDetails', 'payrollNumber', 'defaultBranchId', 'defaultDeskId',
      'workTypes', 'isExactGeo', 'availabilityRestriction'
    ];

    fieldsToCompare.forEach(field => {
      const newValue = getNewValue(field);
      const existingValue = getExistingValue(field);
      fieldChoices[field] = getSmartDefault(field, newValue, existingValue);
    });

    // Handle geometry separately (lat/lng comparison)
    const newGeo = newWorker.geometry;
    const existingGeo = existingWorker.geometry;
    const newHasGeo = newGeo && (newGeo.lat !== 0 || newGeo.lng !== 0);
    const existingHasGeo = existingGeo && (existingGeo.lat !== 0 || existingGeo.lng !== 0);

    if (newHasGeo && existingHasGeo) {
      // Both have coordinates - prefer more exact one, or new if same accuracy
      if (existingWorker.isExactGeo && !newWorker.isExactGeo) {
        fieldChoices['geometry'] = 'existing';
      } else {
        fieldChoices['geometry'] = 'new';
      }
    } else if (newHasGeo) {
      fieldChoices['geometry'] = 'new';
    } else if (existingHasGeo) {
      fieldChoices['geometry'] = 'existing';
    } else {
      fieldChoices['geometry'] = 'existing';
    }

    // Get existing attribute IDs for comparison
    const existingSkillIds = new Set(match.skills.map(s => s.skillId));
    const existingQualIds = new Set(match.qualifications.map(q => q.qualificationId));
    const existingPosIds = new Set(match.positions.map(p => p.positionId));
    const wizardSkillIds = new Set((this.newWorker.worker.matchedSkills || []).map(s => s.attributeId));
    const wizardQualIds = new Set((this.newWorker.worker.matchedQualifications || []).map(q => q.attributeId));
    const wizardPosIds = new Set((this.newWorker.worker.matchedPositions || []).map(p => p.attributeId));

    // For positions that exist in both, initialize field-level choices (default to 'new')
    const positionFieldChoices: Record<string, PositionFieldChoices> = {};
    (this.newWorker.worker.matchedPositions || []).forEach(newPos => {
      if (existingPosIds.has(newPos.attributeId)) {
        positionFieldChoices[newPos.attributeId] = {
          workTypes: 'new',
          source: 'new',
          payRate: 'new',
          isVerified: 'new'
        };
      }
    });

    // For existing-only positions, default to 'keep'
    const existingPositionChoices: Record<string, KeepDeleteChoice> = {};
    match.positions.forEach(p => {
      if (!wizardPosIds.has(p.positionId)) {
        existingPositionChoices[p.positionId] = 'keep';
      }
    });

    // For skills that exist in both, initialize isVerified choices (default to 'new')
    const skillIsVerifiedChoices: Record<string, FieldChoice> = {};
    (this.newWorker.worker.matchedSkills || []).forEach(newSkill => {
      if (existingSkillIds.has(newSkill.attributeId)) {
        skillIsVerifiedChoices[newSkill.attributeId] = 'new';
      }
    });

    // For existing-only skills, default to 'keep'
    const existingSkillChoices: Record<string, KeepDeleteChoice> = {};
    match.skills.forEach(s => {
      if (!wizardSkillIds.has(s.skillId)) {
        existingSkillChoices[s.skillId] = 'keep';
      }
    });

    // For qualifications that exist in both, initialize isVerified choices (default to 'new')
    const qualificationIsVerifiedChoices: Record<string, FieldChoice> = {};
    (this.newWorker.worker.matchedQualifications || []).forEach(newQual => {
      if (existingQualIds.has(newQual.attributeId)) {
        qualificationIsVerifiedChoices[newQual.attributeId] = 'new';
      }
    });

    // For existing-only qualifications, default to 'keep'
    const existingQualificationChoices: Record<string, KeepDeleteChoice> = {};
    match.qualifications.forEach(q => {
      if (!wizardQualIds.has(q.qualificationId)) {
        existingQualificationChoices[q.qualificationId] = 'keep';
      }
    });

    // For existing-only availability slots, default to 'keep'
    const existingAvailabilityChoices: Record<string, KeepDeleteChoice> = {};
    const wizardAvailability = new Set(this.newWorker.worker.availability || []);
    const existingAvailability = existingWorker.availability || [];
    existingAvailability.forEach((slot: string) => {
      if (!wizardAvailability.has(slot)) {
        existingAvailabilityChoices[slot] = 'keep';
      }
    });

    this.mergeSelections = {
      fieldChoices,
      positionFieldChoices,
      skillIsVerifiedChoices,
      qualificationIsVerifiedChoices,
      existingPositionChoices,
      existingSkillChoices,
      existingQualificationChoices,
      existingAvailabilityChoices
    };
  }

  @Mutation
  clearMergeSelections() {
    this.mergeSelections = {
      fieldChoices: {},
      positionFieldChoices: {},
      skillIsVerifiedChoices: {},
      qualificationIsVerifiedChoices: {},
      existingPositionChoices: {},
      existingSkillChoices: {},
      existingQualificationChoices: {},
      existingAvailabilityChoices: {}
    };
  }

  @Mutation
  setFieldChoice(payload: { field: string; choice: FieldChoice }) {
    this.mergeSelections.fieldChoices[payload.field] = payload.choice;
  }

  @Mutation
  setPositionFieldChoice(payload: { positionId: string; field: keyof PositionFieldChoices; choice: FieldChoice }) {
    if (!this.mergeSelections.positionFieldChoices[payload.positionId]) {
      this.mergeSelections.positionFieldChoices[payload.positionId] = {
        workTypes: 'new',
        source: 'new',
        payRate: 'new',
        isVerified: 'new'
      };
    }
    this.mergeSelections.positionFieldChoices[payload.positionId][payload.field] = payload.choice;
  }

  @Mutation
  setExistingPositionChoice(payload: { positionId: string; choice: KeepDeleteChoice }) {
    this.mergeSelections.existingPositionChoices[payload.positionId] = payload.choice;
  }

  @Mutation
  setExistingSkillChoice(payload: { skillId: string; choice: KeepDeleteChoice }) {
    this.mergeSelections.existingSkillChoices[payload.skillId] = payload.choice;
  }

  @Mutation
  setExistingQualificationChoice(payload: { qualificationId: string; choice: KeepDeleteChoice }) {
    this.mergeSelections.existingQualificationChoices[payload.qualificationId] = payload.choice;
  }

  @Mutation
  setExistingAvailabilityChoice(payload: { slot: string; choice: KeepDeleteChoice }) {
    this.mergeSelections.existingAvailabilityChoices[payload.slot] = payload.choice;
  }

  @Mutation
  setSkillIsVerifiedChoice(payload: { skillId: string; choice: FieldChoice }) {
    this.mergeSelections.skillIsVerifiedChoices[payload.skillId] = payload.choice;
  }

  @Mutation
  setQualificationIsVerifiedChoice(payload: { qualificationId: string; choice: FieldChoice }) {
    this.mergeSelections.qualificationIsVerifiedChoices[payload.qualificationId] = payload.choice;
  }
}

export const WorkerWizardModule = getModule(WorkerWizard)

